/**
 * Whether this scorer is asked where each shot went, for this match (ADR-0022).
 *
 * Two states, on and off. Kept on the device and not in the match, on purpose. It is a preference
 * about how *this* scorer wants to work, not a fact about the match: a second scorer on another
 * phone is free to choose differently, and nothing about the score depends on it. Putting it in
 * the setup would also have put it in `publish.ts`'s payload for no one's benefit.
 *
 * **Off by default.** A scorer who never asked for this keeps the one-tap pad (PRD §6.8). It can
 * be chosen when the match is set up and **changed at any point during it**, from the pad: a
 * scorer who forgot can turn it on in the third over, and the balls from then on carry a direction.
 */
import type { Store } from "../localStore.ts";

export type WagonMode = "off" | "on";

export const DEFAULT_WAGON_MODE: WagonMode = "off";

export const WAGON_MODES: readonly { readonly mode: WagonMode; readonly label: string }[] = [
  { mode: "off", label: "Off" },
  { mode: "on", label: "On" },
];

const keyOf = (matchId: string): string => `sportsos.wagon.${matchId}`;

/**
 * The stored choice, or off. Never throws: a corrupt value is the default, not a broken pad.
 *
 * An earlier build offered "4s & 6s" and "every scoring shot", stored as `boundaries` and `all`.
 * Both mean the scorer wanted the wheel, so both read as on rather than silently switching it off
 * under someone mid-match.
 */
export function wagonMode(store: Store, matchId: string): WagonMode {
  try {
    const raw = store.getItem(keyOf(matchId));
    return raw === "on" || raw === "all" || raw === "boundaries" ? "on" : DEFAULT_WAGON_MODE;
  } catch {
    return DEFAULT_WAGON_MODE;
  }
}

export function setWagonMode(store: Store, matchId: string, mode: WagonMode): void {
  try {
    store.setItem(keyOf(matchId), mode);
  } catch {
    // Storage full or refused. The choice lasts until the screen is left, which is a small loss.
  }
}

/** The other state, for the pad's switch. */
export const toggleWagonMode = (mode: WagonMode): WagonMode => (mode === "off" ? "on" : "off");

export const wagonModeLabel = (mode: WagonMode): string => (mode === "on" ? "On" : "Off");
